import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@roberttamas/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Plus, Trash2 } from "@/components/icons";
import type { SetupStepProps } from "@/components/setup/steps";
import { StepSaveButton, YesNoStep } from "@/components/setup/yes-no-step";
import { updateMySiteConfig } from "@/lib/api";
import { makeId } from "@/lib/draft-ids";
import { toAppError } from "@/lib/errors";
import {
  DEFAULT_TIMELINE_ICON,
  TIMELINE_ICON_OPTIONS,
} from "@/lib/timeline-icons";
import type {
  TimelineConfig,
  TimelineIconKey,
} from "@/lib/types";

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

interface DraftEvent {
  id: string;
  time: string;
  name: string;
  icon: TimelineIconKey;
}

interface DraftDay {
  id: string;
  date: string;
  events: DraftEvent[];
}

function emptyEvent(time = ""): DraftEvent {
  return { id: makeId(), time, name: "", icon: DEFAULT_TIMELINE_ICON };
}

function toDraft(
  timeline: TimelineConfig | undefined,
  weddingDate: string | null,
): DraftDay[] {
  const days = (timeline?.days ?? []).map((d) => ({
    id: d.id,
    date: d.date,
    events: d.events.map((e) => ({
      id: e.id,
      time: e.time,
      name: e.name,
      icon: e.icon,
    })),
  }));
  if (days.length > 0) return days;
  return [
    {
      id: makeId(),
      date: weddingDate ?? "",
      events: [emptyEvent("12:00")],
    },
  ];
}

/**
 * Wizard timeline step — "Do you want a day schedule?" then a simple
 * one-day event list prefilled with the wedding date (extra days are
 * one tap away). No timezone UI: Europe/Prague, the audience's home.
 */
export function StepTimeline({
  orderId,
  setup,
  linear,
  onDone,
  onBack,
}: SetupStepProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { site, config } = setup;
  const siteId = site?.siteId ?? "";

  const [enabled, setEnabled] = useState<boolean | null>(
    config?.permissions?.allowTimeline === undefined && !config?.timeline
      ? null
      : (config?.permissions?.allowTimeline ?? false),
  );
  const [days, setDays] = useState<DraftDay[]>(() =>
    toDraft(config?.timeline, site?.weddingDate ?? null),
  );

  const updateDay = (id: string, patch: Partial<DraftDay>) =>
    setDays((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  const updateEvent = (
    dayId: string,
    eventId: string,
    patch: Partial<DraftEvent>,
  ) =>
    setDays((prev) =>
      prev.map((d) =>
        d.id === dayId
          ? {
              ...d,
              events: d.events.map((e) =>
                e.id === eventId ? { ...e, ...patch } : e,
              ),
            }
          : d,
      ),
    );

  const mutation = useMutation({
    mutationFn: () => {
      if (enabled === false) {
        return updateMySiteConfig({
          siteId,
          configPatch: { permissions: { allowTimeline: false } },
        });
      }
      const cleanDays = days
        .filter((d) => d.date && d.events.some((e) => e.name.trim()))
        .map((d) => ({
          id: d.id,
          date: d.date,
          events: d.events
            .filter((e) => e.name.trim() && TIME_RE.test(e.time))
            .map((e) => ({
              id: e.id,
              time: e.time,
              name: e.name.trim(),
              icon: e.icon,
            })),
        }));
      const timeline: TimelineConfig = {
        timeZone: "Europe/Prague",
        showDates: cleanDays.length > 1,
        days: cleanDays,
      };
      return updateMySiteConfig({
        siteId,
        configPatch: { timeline, permissions: { allowTimeline: true } },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["mySetup", orderId] });
      onDone();
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  const save = () => {
    if (enabled === null) {
      toast.error(t("setup.pickYesNo"));
      return;
    }
    if (enabled) {
      const hasEvent = days.some(
        (d) => d.date && d.events.some((e) => e.name.trim() && TIME_RE.test(e.time)),
      );
      if (!hasEvent) {
        toast.error(t("setup.timeline.addEventWithTime"));
        return;
      }
    }
    mutation.mutate();
  };

  return (
    <YesNoStep
      stepId="timeline"
      yesLabel={t("setup.timeline.yes")}
      noLabel={t("setup.timeline.no")}
      enabled={enabled}
      onChoose={setEnabled}
      onBack={onBack}
      footer={
        <StepSaveButton
          saving={mutation.isPending}
          disabled={enabled === null}
          linear={linear}
          onClick={save}
        />
      }
    >
      <div className="space-y-5">
        {days.map((day, di) => (
          <div key={day.id} className="space-y-3 rounded-lg border p-4">
            <div className="flex items-end gap-2">
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-xs uppercase text-muted-foreground">
                  {di === 0 ? t("setup.timeline.bigDay") : t("setup.timeline.dayN", { n: di + 1 })}
                </p>
                <Input
                  type="date"
                  value={day.date}
                  onChange={(e) => updateDay(day.id, { date: e.target.value })}
                />
              </div>
              {days.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={t("timelineEditor.removeDay")}
                  onClick={() =>
                    setDays((prev) => prev.filter((d) => d.id !== day.id))
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="space-y-2">
              {day.events.map((event) => (
                <div key={event.id} className="flex items-center gap-2">
                  <Input
                    type="time"
                    className="w-28"
                    value={event.time}
                    onChange={(e) =>
                      updateEvent(day.id, event.id, { time: e.target.value })
                    }
                  />
                  <Input
                    placeholder={t("setup.timeline.eventPlaceholder")}
                    value={event.name}
                    onChange={(e) =>
                      updateEvent(day.id, event.id, { name: e.target.value })
                    }
                  />
                  <Select
                    value={event.icon}
                    onValueChange={(icon) =>
                      updateEvent(day.id, event.id, {
                        icon: icon as TimelineIconKey,
                      })
                    }
                  >
                    <SelectTrigger className="w-36 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMELINE_ICON_OPTIONS.map((opt) => (
                        <SelectItem key={opt.key} value={opt.key}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {day.events.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={t("timelineEditor.removeEvent")}
                      onClick={() =>
                        updateDay(day.id, {
                          events: day.events.filter((e) => e.id !== event.id),
                        })
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  updateDay(day.id, {
                    events: [
                      ...day.events,
                      emptyEvent(day.events[day.events.length - 1]?.time ?? ""),
                    ],
                  })
                }
              >
                <Plus className="h-4 w-4" /> {t("timelineEditor.addEvent")}
              </Button>
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            setDays((prev) => [
              ...prev,
              { id: makeId(), date: "", events: [emptyEvent()] },
            ])
          }
        >
          <Plus className="h-4 w-4" /> {t("setup.timeline.addDay")}
        </Button>
      </div>
    </YesNoStep>
  );
}
